import Image from "next/image";
import Link from "next/link";
import type { Product, PopulatedCategory } from "@/Types/responses/product";
import { ArrowRight } from "lucide-react";

type ProductCardProps = {
  product: Product;
  view?: "grid" | "list";
  className?: string;
};

// A product's categories can include entries at different depths (a
// top-level category and its own subcategory both tagged on the same
// product). Array order isn't meaningful, so pick the deepest categoryPath
// as the canonical one — otherwise a top-level category at index 0 can
// truncate the URL/breadcrumb to a single level.
function getPrimaryCategory(categories: PopulatedCategory[] = []) {
  if (!categories.length) return undefined;
  return categories.reduce((deepest, cat) =>
    (cat.categoryPath?.split("/").length ?? 0) > (deepest.categoryPath?.split("/").length ?? 0)
      ? cat
      : deepest
  , categories[0]);
}

export default function ProductCard({ product, view = "grid", className = "", }: ProductCardProps) {

  const isList = view === "list";

  const primaryCategory = getPrimaryCategory(product.categories);
  const href = primaryCategory
    ? `/shop/${primaryCategory.categoryPath}/${product.slug}`
    : `/shop/${product.slug}`;

  return (
    <Link
      href={href}
      className={`group flex ${
        isList ? "flex-row items-center gap-4 p-0" : "flex-col gap-2 p-0"
      } ${className}`}
    >
      <div
        className={`relative shrink-0 overflow-hidden bg-neutral-100 ${
          isList ? "h-24 w-24" : "aspect-square w-full"
        }`}
      >
        <Image
          src={product.featured_image.url}
          alt={product.name}
          fill
          sizes={
            isList
              ? "96px"
              : "(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
          }
          className="object-cover transition-transform duration-200 group-hover:scale-105"
        />
      </div>

      <div
        className={`flex items-stretch gap-2 
          ${ isList ? "flex-1 justify-between" : ""
        }`}
      >
        <div className="flex flex-1 items-start justify-start flex-wrap">
          <span
            className={`font-medium text-neutral-800 ${
              isList ? "line-clamp-1 text-sm md:text-lg" : "line-clamp-2 text-sm md:text-lg"
            }`}
          >
            {product.name}
          </span>
        </div>

        <div className="flex items-center w-10">
          <ArrowRight className="shrink-0 -rotate-45 md:w-6 md:h-6 text-primary"/>
        </div>
      
      </div>
    </Link>
  );
}